import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { saveAs } from 'file-saver';
import {
  get,
  find,
  findIndex,
  isEmpty,
  isArray,
  capitalize,
} from 'lodash';
import {
  Outlet,
  Link,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router';
import {
  API,
  OrderHelper,
  OrderTypeHelper,
  NavigationHelper,
  ColorUtil,
  // StorageUtil,
  OrderModel,
  SegmentModel,
  Field2 as Field,
  Submenu,
  Tag,
  TableSegments,
  // VibeAccordion,
  VibeModal,
  VibeAlert,
  VibeIcon,
  viCopy,
  viPlayCircleOutline,
  viPauseCircleOutline,
  viTrash,
  viTrendingUp,
  viClose,
  viEye,
  viDoDisturbAlt,
  viArchive,
  color,
} from 'vibeguide';
import C, { lower } from 'constants';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import SubNavigation from '../../../Header/SubNavigation';
import OrderInformation from './OrderInformation';
import OrderNotes from './OrderNotes';
import ApproverInformation from '../../Flights/ApproverInformation';
import OrderAllocationOverview from './OrderAllocationOverview';

const BootstrapButton = styled(Button)({
  border: `1px solid ${color.manatee}`,
  color: color.gray700,
  textTransform: 'none',
  fontFamily: [
    '-apple-system',
    'Lato',
    'sans-serif"',
  ].join(','),
});

function OrderDetails({
  className = '',
  style = {},
  user,
}) {
  const [order, setOrder] = useState({
    data: new OrderModel(),
    segments: [],
  });

  /**
   * Accepted Views: "impression" or "spot"
   * default to impression, automatically set to spot after order is fetched if criteria is met
   */
  const [currentView, setCurrentView] = useState('impression');

  /**
   * Modals need their own state for the types that require a "statusReason"
   * Otherwise the state is lost if using the "confirmModal" object method
   */
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmStopCancel, setConfirmStopCancel] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  // const [accordionExpanded, setAccordionExpanded] = useState(StorageUtil.getLocal('orders:expanded', true));
  const [cancelOption, setCancelOption] = useState('cancel_now');
  const [futureCancelDate, setFutureCancelDate] = useState(null);

  const location = useLocation();
  const isGraph = location.pathname.indexOf('/graph') >= 0;
  // is the current view impression or spot based?
  const isImpression = currentView === 'impression';

  const {
    id: orderId,
    type,
  } = useParams();

  const history = useNavigate();

  // is the order scheduled to be cancelled?
  const cancelOrderScheduled = get(order, 'data.status') !== 'cancelled'
    && !isEmpty(get(order, 'data.scheduledCancellationDate'));

  let breadcrumbs = [
    {
      title: 'Dashboard',
      linkUrl: '../../orders/dashboard',
    },
  ];

  /**
   * Get Order Segments
   */
  const getSegments = async (options = {}) => {
    // if user cannot view segments skip
    if (!user.can('order_segment.view')) {
      return [];
    }

    const params = NavigationHelper.getParams();
    const active = get(params, 'active') !== 'false';

    const postOptions = options.csv === true
      ? {
        headers: {
          Accept: 'text/csv',
        },
      }
      : {};

    // get all segments so we can calculate delivered data
    const response = await API.Order.Segment.list(
      {
        _id: orderId,
        filters: {
          active,
        },
        pageSize: -1,
      },
      postOptions,
    );

    // return CSV string
    if (options.csv === true) {
      return response;
    }

    const segmentItems = get(response, 'data', []).map((segment) => {
      return new SegmentModel(segment);
    });

    return segmentItems;
  };

  /**
   * Get Order Data
   */
  const getOrderData = async () => {
    const response = await API.Order.get(orderId);
    return new OrderModel(response);
  };

  /**
   * Get the Order Details
   */
  const getOrder = async () => {
    if (!orderId) {
      console.error('Order ID not Specified');
      return;
    }

    const orderData = await getOrderData();
    const orderSegments = await getSegments();

    if (orderSegments && orderSegments.length > 0) {
      // check if all order segments are spots-per-hour
      const spotsPerHourSegments = orderSegments.filter(segment => segment.spotSelectionStrategy === 'spots-per-hour');
      if (orderSegments.length === spotsPerHourSegments.length) {
        // all segments are spots-per-hour, automatically switch the view to spot based
        setCurrentView('spot');
      }
    }

    setOrder({
      data: orderData,
      segments: orderSegments,
    });
  };

  /**
   * Reset the pause order modal form
   */
  const resetPauseModal = () => {
    setConfirmPause(false);
    setStatusReason('');
  };

  /**
   * Reset the cancel order modal form
   */
  const resetCancelModal = () => {
    setConfirmCancel(false);
    setStatusReason('');
    setCancelOption('cancel_now');
    setFutureCancelDate(null);
  };

  /**
   * Reset the stop cancellation order modal form
   */
  const resetStopCancelModal = () => {
    setConfirmStopCancel(false);
  };

  /**
   * Export to CSV file
   */
  const onExport = async () => {
    // get all segments so we can merge with flight summary data
    const response = await getSegments({ csv: true });

    const blob = new Blob([response], { type: 'text/csv;charset=utf-8' });
    const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
    saveAs(blob, `segments-${timestamp}.csv`);
  };

  /**
   * When the user changes the reason textarea in the confirm modal
   */
  const onChangeStatusReason = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    setStatusReason(value);
  };

  /**
   * Copy Order
   */
  const onConfirmCopy = async () => {
    const response = await API.Order.copy({
      _id: orderId,
    });

    const success = get(response, '[0].type') === 'FLIGHT.CREATED';

    // redirect to the copied order (which is now a flight)
    if (success) {
      const copyId = get(response, '[0].documentId');
      history(`/ad-ops/${lower(C.PROPOSAL)}/${copyId}`);
    }
  };

  /**
   * Cancel Copy
   */
  const onCancelCopy = () => {
    setConfirmCopy(false);
  };

  /**
   * Pause Order
   */
  const onConfirmPause = async () => {
    const response = await API.Order.pause({
      _id: orderId,
      statusReason,
    });

    const success = get(response, '[0].type') === 'ORDER.PAUSED';

    if (success) {
      resetPauseModal();
      getOrder();
    }
  };

  /**
   * Cancel Pause
   */
  const onCancelPause = () => {
    resetPauseModal();
  };

  /**
   * Resume Order
   */
  const onConfirmResume = async () => {
    const response = await API.Order.resume({
      _id: orderId,
    });

    const success = get(response, '[0].type') === 'ORDER.RESUMED';

    if (success) {
      setConfirmResume(false);
      getOrder();
    }
  };

  /**
   * Cancel Resume
   */
  const onCancelResume = () => {
    setConfirmResume(false);
  };

  /**
   * Cancel Order
   */
  const onConfirmCancel = async () => {
    const cancelData = {
      _id: orderId,
      statusReason,
    };

    // schedule the cancellation for the future
    if (cancelOption === 'schedule') {
      cancelData.scheduledCancellationDate = futureCancelDate;
    }

    const response = await API.Order.cancel(cancelData);
    const responseType = get(response, '[0].type');
    const success = responseType === 'ORDER.CANCELLED'
      || responseType === 'ORDER.CANCELLATION_SCHEDULED';

    if (success) {
      resetCancelModal();
      getOrder();
    }
  };

  /**
   * Cancel "Canceling" an Order
   */
  const onCancelCancel = () => {
    resetCancelModal();
  };

  /**
   * Stop Cancellation of an Order
   */
  const onConfirmStopCancel = async () => {
    const response = await API.Order.stopCancellation({
      _id: orderId,
    });
    const responseType = get(response, '[0].type');
    const success = responseType === 'ORDER.CANCELLATION_STOPPED';

    if (success) {
      resetStopCancelModal();
      getOrder();
    }
  };

  /**
   * Close the Stop Cancellation Modal
   */
  const onCancelStopCancel = () => {
    resetStopCancelModal();
  };

  /**
   * When the Order Information Accordion is changed
   */
  // const onChangeAccordion = (e, expanded) => {
  //   StorageUtil.setLocal('orders:expanded', expanded);
  //   setAccordionExpanded(expanded);
  // };

  /**
   * Get the Buttons to use for the Dropdown
   */
  const getMenuItems = () => {
    const orderStatus = get(order, 'data.status');
    // open-exchange orders are managed via location opt-in, not the order/flight UI.
    // They cannot be copied or cancelled (backend rejects cancel with 400); pause instead.
    const isOpenExchange = get(order, 'data.orderType') === 'open-exchange';

    const items = isOpenExchange
      ? []
      : [{
        text: 'Copy',
        userCan: [
          'flight.create',
        ],
        icon: viCopy,
        color: 'primary',
        onClick: () => setConfirmCopy(true),
      }];

    if (orderStatus === 'paused') {
      // Can only resume orders that have been paused
      items.push({
        text: 'Resume Order',
        userCan: [
          'order.pause',
        ],
        icon: viPlayCircleOutline,
        color: color.primary,
        onClick: () => setConfirmResume(true),
      });
    } else if (orderStatus === 'live') {
      // Can only pause live orders
      items.push({
        text: 'Pause Order',
        userCan: [
          'order.pause',
        ],
        icon: viPauseCircleOutline,
        color: color.primary,
        onClick: () => setConfirmPause(true),
      });
    }

    // show cancel order option if the order is not cancelled or completed
    // and the order is not scheduled to be cancelled.
    // open-exchange orders cannot be cancelled, so omit cancellation options entirely.
    if (isOpenExchange) {
      return items;
    }

    if (orderStatus !== 'cancelled'
      && orderStatus !== 'completed'
      && !cancelOrderScheduled
    ) {
      // Can only cancel orders that haven't been completed or canceled already
      items.push({
        text: 'Cancel Order',
        userCan: [
          'order.cancel',
        ],
        icon: viTrash,
        color: color.error,
        onClick: () => setConfirmCancel(true),
      });
    } else if (cancelOrderScheduled) {
      // order is scheduled to be cancelled, show options to stop or change the cancellation
      items.push({
        text: 'Stop Cancellation',
        userCan: [
          'order.cancel',
        ],
        icon: viClose,
        color: color.error,
        onClick: () => setConfirmStopCancel(true),
      });

      items.push({
        text: 'Edit Cancellation',
        userCan: [
          'order.cancel',
        ],
        icon: viArchive,
        color: color.error,
        onClick: () => {
          // set the cancellation modal fields
          setCancelOption('schedule');
          setFutureCancelDate(get(order, 'data.scheduledCancellationDate', null));
          setStatusReason(get(order, 'data.statusReason', ''));
          setConfirmCancel(true);
        },
      });
    }

    return items;
  };

  /**
   * Get total number of delivered impression and spots
   */
  const getOrderSegmentData = () => {
    const segments = get(order, 'segments', []);

    return {
      deliveredImpressions: segments.reduce((sum, { deliveredImpressions }) => sum + deliveredImpressions, 0),
      deliveredSpots: segments.reduce((sum, { deliveredSpots }) => sum + deliveredSpots, 0),
      segmentType: find(segments, { segmentType: 'added-value' }) !== undefined
        ? 'added-value'
        : 'standard',
    };
  };

  /**
   * Get Order Type
   */
  const getOrderTypeItem = (orderData) => {
    return find(OrderTypeHelper.list(), { value: orderData.orderType }) || {};
  };

  /**
   * Get the page content
   */
  const getPageContent = () => {
    const isOpenExchangeOrder = get(order, 'data.orderType') === 'open-exchange';

    switch (type) {
      case 'segments':
        return (
          <TableSegments
            columns={columns}
            collection={orderSegments}
            defaultSortBy={{
              label: 'Name',
              attr: 'name',
              direction: 'asc',
            }}
            paginator
            paginatorProps={{
              label: 'Segments',
              urlPaging: true,
              urlFilters: true,
            }}
            rowLink="../segment/{_id}"
            csv
            csvProps={{
              onExport,
            }}
            // include segment summary data to show warnings and rollup information
            segmentSummaries={orderSegments}
            adProvider={orderData.adProvider}
          />
        );

      case 'allocations':
        return (
          <OrderAllocationOverview
            orderType={orderData.orderType}
            orderId={orderData._id}
            currencyCode={orderData.currencyCode}
          />
        );

      case 'overview':
      default:
        return (
          <>
            <div
              style={{
                margin: '0 2rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 24,
                }}
              >
                <div
                  style={{
                    width: '50%',
                  }}
                >
                  <OrderInformation
                    orderType={orderData.orderType}
                    opportunityName={orderData.opportunityName}
                    opportunityId={orderData.opportunityId}
                    dealId={orderData.dealId}
                    extReferenceId={orderData.extReferenceId}
                    agency={orderData.agency}
                    category={orderData.category}
                    deliverySystem={orderData.deliverySystem}
                    adProvider={orderData.adProvider}
                    advertiserName={orderData.advertiserName}
                    programmaticBuyer={orderData.programmaticBuyer}
                    orderStartDate={orderData.startDate}
                    orderEndDate={orderData.endDate}
                    // show the cancellation date (or future cancellation date)
                    orderCancellationDate={orderData.cancellationDate || orderData.scheduledCancellationDate}
                    orderStatus={orderData.status}
                  />
                </div>

                {!isOpenExchangeOrder && (
                  <div
                    style={{
                      width: '50%',
                    }}
                  >
                    <ApproverInformation
                      flightData={orderData}
                      flightSegments={orderSegments}
                      title="Approver Information"
                      isOrder
                    />
                  </div>
                )}
              </div>

              <OrderNotes
                orderId={orderData._id}
                notes={orderData.activity}
                canAddNote={user.can('order.modify')}
              />
            </div>
          </>
        );
    }
  };

  /**
   * When the user clicks on the impression values button
   */
  const onClickImpressionValues = () => {
    setCurrentView('impression');
  };

  /**
   * When the user clicks on the spot values button
   */
  const onClickSpotValues = () => {
    setCurrentView('spot');
  };

  /**
   * When the Order ID Changes
   */
  useEffect(() => {
    getOrder();
  }, [orderId]);

  const updateTitle = (status) => {
    return `${capitalize(status)} Orders`;
  };

  // merge some segment data into the order (delivered impressions, etc)
  const orderData = {
    ...order.data,
    ...getOrderSegmentData(),
  };

  const orderSegments = order.segments;
  const isViewOnly = orderData.partnerAccessLevel === 'view-all'
    || orderData.partnerAccessLevel === 'view-limited'
    || orderData.partnerAccessLevel === 'none';

  const buildBreadcrumbs = (name, status) => {
    const orderTypeItem = getOrderTypeItem(orderData);

    breadcrumbs = [
      ...breadcrumbs,
      {
        title: updateTitle(status),
        linkUrl: `../../orders/dashboard/${status}`,
      },
      {
        title: name,
        icon: orderTypeItem.icon,
        iconTooltip: orderTypeItem.label,
        linkUrl: './',
      },
    ];
  };

  // do not render until an order has been fetched
  if (!orderData._id) {
    return null;
  }

  buildBreadcrumbs(
    orderData.name,
    orderData.status,
  );

  // open-exchange orders are not budgeted (zeroed budget / impressions), so they use the
  // same reduced column set as always-on-pmp (no Budget / Target Impressions columns).
  const isOpenExchangeOrder = orderData.orderType === 'open-exchange';
  const isNonBudgetedOrder = orderData.orderType === 'always-on-pmp'
    || isOpenExchangeOrder;

  const columns = isNonBudgetedOrder
    ? [
      'Name',
      'Media Format',
      'Start Date',
      'End Date',
      'Start Time',
      'End Time',
      'CPM',
      'CPM Type',
      'Total Impressions',
      'Total Spots',
      'Spot Type',
      'Allow Duplicate Ads Back to Back',
      'Days of Week',
      'Created By',
      'Created Date',
      '...',
    // open-exchange uses sentinel always-on dates, so hide Start Date / End Date columns
    ].filter(column => !isOpenExchangeOrder || (column !== 'Start Date' && column !== 'End Date'))
    : [
      'Name',
      'Media Format',
      'Segment Type',
      'Creative',
      'Start Date',
      'End Date',
      'Start Time',
      'End Time',
      'Budget',
      'CPM',
      'Target Impressions',
      'Total Impressions',
      'Total Spots',
      'Frequency',
      'Frequency Cap',
      'Spot Type',
      'Spot Selection Strategy',
      'Allocate Remainder',
      'Allow Duplicate Ads Back to Back',
      'Days of Week',
      'Currency',
      'Created By',
      'Created Date',
      '...',
    ];

  // add impression columns for Vibenomics orders
  if (isImpression && orderData.reportingAuthority === 'vibenomics') {
    // add before the Start Date column (or Start Time when Start Date is hidden, e.g. open-exchange)
    const columnStartDateIndex = findIndex(columns, (column => column === 'Start Date'));
    const insertIndex = columnStartDateIndex >= 0
      ? columnStartDateIndex
      : findIndex(columns, (column => column === 'Start Time'));
    columns.splice(insertIndex, 0, 'Impression Progress');
  }

  // add cancelled date for cancelled orders
  if (orderData.status === 'cancelled') {
    // add before the Start Time column
    const columnStartTimeIndex = findIndex(columns, (column => column === 'Start Time'));
    columns.splice(columnStartTimeIndex, 0, 'Cancelled Date');
  }

  return (
    <div
      className={classNames('OrderDetails', className)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'auto',
        ...style,
      }}
    >
      <SubNavigation
        breadcrumbs={breadcrumbs}
        statusTag={{
          label: orderData.status,
          style: find(OrderHelper.statusList, { value: orderData.status }).tagStyle,
          // show the order status reason in the tooltip unless the order is scheduled to be cancelled
          // in which case show the scheduled cancellation date
          tooltip: cancelOrderScheduled
            ? `Scheduled Cancellation: ${orderData.scheduledCancellationDate}`
            : orderData.statusReason,
          // show cancelled icon when the order is scheduled to be cancelled
          icon: cancelOrderScheduled
            ? viDoDisturbAlt
            : null,
        }}
        buttons={!isViewOnly
          ? getMenuItems()
          : []}
        isDropMenu
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          margin: '0 2rem',
          marginBottom: orderData.reportingAuthority !== 'vibenomics'
            // add spacing when the info cards aren't shown between the order dates and the table
            ? '2rem'
            // spacing is already added with the info cards
            : 0,
        }}
      >
        {orderData.reportingAuthority === 'vibenomics' && (
          <ButtonGroup variant="outlined" color="inherit">
            <BootstrapButton
              variant={isImpression
                ? 'contained'
                : 'outlined'}
              disableElevation
              onClick={onClickImpressionValues}
              data-testid="order-view-impression-values-button"
            >
              Impression Values
            </BootstrapButton>

            <BootstrapButton
              variant={!isImpression
                ? 'contained'
                : 'outlined'}
              disableElevation
              onClick={onClickSpotValues}
              data-testid="order-view-spot-values-button"
            >
              Spot Values
            </BootstrapButton>
          </ButtonGroup>
        )}

        {isViewOnly && (
          <Tag
            tag={{
              name: 'View-Only',
              icon: viEye,
            }}
            style={{
              background: color.primary16,
              color: color.primary,
              fontSize: 12,
            }}
          />
        )}

        {orderData.reportingAuthority === 'vibenomics' && (
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Link
              to={isGraph
                ? '.'
                : './graph'}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: color.primary,
              }}
            >
              {isGraph
                ? 'Close Graph'
                : 'View Graph'}

              <VibeIcon
                icon={isGraph
                  ? viClose
                  : viTrendingUp}
                color={color.teal}
                hoverColor={ColorUtil.transparent(color.teal, 50)}
                size={24}
              />
            </Link>
          </div>
        )}
      </div>

      {orderData.reportingAuthority === 'vibenomics' && (
        <Outlet
          context={[orderData, currentView]}
        />
      )}

      <Submenu
        rootUrl={`/ad-ops/order/${orderData._id}`}
        matchParameter="tab"
        items={[
          {
            name: 'Details',
            label: 'Overview',
            show: true,
            enabled: user.can('order.view'),
          },
          {
            name: 'Segments',
            show: true,
            enabled: user.can('order_segment.view'),
          },
          {
            name: 'Allocations',
            label: orderData.orderType === 'always-on-pmp' || orderData.orderType === 'open-exchange'
              ? 'Delivery'
              : 'Allocations',
            icon: isArray(orderData.warnings) && orderData?.warnings?.length > 0 ? (
              <VibeAlert
                severity="warning"
                alerts={orderData.warnings || []}
                size={24}
                iconOnly
              />
            ) : null,
            show: true,
            enabled: user.can('order.view'),
          },
        ]}
      />

      <div
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          marginTop: '2rem',
        }}
      >
        {getPageContent()}
      </div>

      {/* Copy an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmCopy}
        title="Copy Order"
        text={`Are you sure you want to copy ${orderData.name}?`}
        testIds={{
          root: 'order-copy-dialog',
          confirm: 'order-copy-confirm-button',
          cancel: 'order-copy-cancel-button',
        }}
        onConfirm={onConfirmCopy}
        onClose={onCancelCopy}
      />

      {/* Pause an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmPause}
        confirmProps={{
          disabled: isEmpty(statusReason),
        }}
        title="Pause Order"
        text={(
          <div>
            <div
              style={{
                marginBottom: 16,
              }}
            >
              Are you sure you want to pause {orderData.name}?
            </div>

            <Field
              type="textarea"
              style={{
                height: 150,
              }}
              label="Reason"
              placeholder="Reason for Pausing..."
              value={statusReason}
              testId="order-pause-reason-input"
              onChange={onChangeStatusReason}
              autoFocus
              required
            />
          </div>
        )}
        testIds={{
          root: 'order-pause-dialog',
          confirm: 'order-pause-confirm-button',
          cancel: 'order-pause-cancel-button',
        }}
        onConfirm={onConfirmPause}
        onClose={onCancelPause}
      />

      {/* Resume an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmResume}
        title="Resume Order"
        text={`Are you sure you want to resume ${orderData.name}?`}
        testIds={{
          root: 'order-resume-dialog',
          confirm: 'order-resume-confirm-button',
          cancel: 'order-resume-cancel-button',
        }}
        onConfirm={onConfirmResume}
        onClose={onCancelResume}
      />

      {/* Cancel an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmCancel}
        confirmProps={{
          color: 'error',
          disabled: isEmpty(statusReason)
            || (cancelOption === 'schedule' && !futureCancelDate),
        }}
        cancelProps={{
          text: 'Dismiss',
        }}
        title="You are cancelling this order"
        width={500}
        testIds={{
          root: 'order-cancel-dialog',
          confirm: 'order-cancel-confirm-button',
          cancel: 'order-cancel-cancel-button',
        }}
        onConfirm={onConfirmCancel}
        onClose={onCancelCancel}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <Field
            type="radio"
            label="Cancel now"
            radioProps={{
              selected: cancelOption === 'cancel_now',
              label: 'now',
            }}
            testId="order-cancel-now-option"
            onClick={() => setCancelOption('cancel_now')}
          />

          <Field
            type="radio"
            label="Schedule"
            radioProps={{
              selected: cancelOption === 'schedule',
              label: 'schedule',
              cancelOption,
            }}
            testId="order-cancel-schedule-option"
            onClick={() => setCancelOption('schedule')}
          />

          {cancelOption === 'schedule' && (
            <Field
              rootStyle={{
                width: '50%',
              }}
              type="datepicker"
              label="Future Cancellation Date"
              placeholder="YYYY-MM-DD"
              value={futureCancelDate}
              dateProps={{
                format: 'YYYY-MM-DD',
                minDate: moment().toDate(),
              }}
              testId="order-cancel-future-date-input"
              onChange={(futureDate) => setFutureCancelDate(futureDate)}
              required
            />
          )}

          <Field
            type="textarea"
            style={{
              height: 150,
            }}
            label="Reason for Cancelling"
            placeholder="Enter a description..."
            value={statusReason}
            testId="order-cancel-reason-input"
            onChange={onChangeStatusReason}
            autoFocus
            required
          />
        </div>
      </VibeModal>

      {/* Stop Cancellation of an Order Modal */}
      <VibeModal
        type="confirm"
        show={confirmStopCancel}
        cancelProps={{
          text: 'Dismiss',
        }}
        title="You are stopping a future cancellation"
        width={500}
        text="Your order will no longer be cancelled"
        testIds={{
          root: 'order-stop-cancellation-dialog',
          confirm: 'order-stop-cancellation-confirm-button',
          cancel: 'order-stop-cancellation-cancel-button',
        }}
        onConfirm={onConfirmStopCancel}
        onClose={onCancelStopCancel}
      />
    </div>
  );
}

OrderDetails.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(OrderDetails);
